/**
 * Platform-neutral `presentationSpec` semantics.
 *
 * Deliberately free of DOM and react-native imports so the desktop web editor
 * and the mobile read-only viewer render the same deck from one definition.
 * Themes and slide geometry used to live only inside the `.web.jsx` editor,
 * which meant a native viewer had to copy them and then silently drift.
 *
 * Slides are positioned in INCHES on a 13.333" x 7.5" surface (PowerPoint's
 * LAYOUT_WIDE), matching what `backend/utils/presentation_to_pptx.js` writes.
 * Rendering is driven entirely by `slide.elements`; `slide.layout` is authoring
 * metadata that neither renderer branches on.
 */

import { fitCompactSlideText } from './presentation_text_fit';

export const SLIDE_WIDTH = 13.333;
export const SLIDE_HEIGHT = 7.5;
export const MAX_BULLETS = 8;

// Chart + table slide elements. Caps mirror the server-side normalizers in
// backend/utils/presentation_to_pptx.js so client edits survive a round-trip.
export const CHART_ELEMENT_TYPES = ['bar', 'line', 'pie', 'area'];
export const MAX_CHART_SERIES = 4;
export const MAX_CHART_POINTS = 12;
export const MAX_TABLE_COLS = 8;
export const MAX_TABLE_ROWS = 12;
export const MAX_TABLE_CELL_CHARS = 120;

// Default series colors for slide charts (first slot is the theme accent).
export const CHART_SERIES_COLORS = ['#4c78e8', '#4c9b5f', '#e89b3c', '#9b59b6', '#e45756', '#54a7b0'];

// The web editor sizes text as `fontSize / 9.6` container-query width units,
// i.e. one `fontSize` unit per 1/960th of the rendered slide width. Scaling
// against the same basis keeps native text proportional to the real slide.
export const SLIDE_REFERENCE_WIDTH = 960;

export const DEFAULT_PRESENTATION_THEME = 'clean-modern';

export const PRESENTATION_THEMES = {
  'clean-modern': { background: '#ffffff', title: '#111827', text: '#1f2937', accent: '#2563eb', surface: '#f3f4f6' },
  'bold-startup': { background: '#0f172a', title: '#ffffff', text: '#e2e8f0', accent: '#38bdf8', surface: '#1a2744' },
  corporate: { background: '#ffffff', title: '#0f2a43', text: '#1e3348', accent: '#1d4ed8', surface: '#f0f4f8' },
  minimal: { background: '#fafafa', title: '#18181b', text: '#27272a', accent: '#71717a', surface: '#f0f0f1' },
  classroom: { background: '#fffdf5', title: '#78350f', text: '#3a342e', accent: '#d97706', surface: '#f7f1dc' },
  harbor: { background: '#0c1822', title: '#f2ede3', text: '#d2dce2', accent: '#c4a574', surface: '#152330' },
  ink: { background: '#f4efe4', title: '#1b1814', text: '#3f3a34', accent: '#6e2a28', surface: '#ebe4d4' },
  atelier: { background: '#e8ede6', title: '#1a2a21', text: '#2f3c34', accent: '#2a5640', surface: '#dde4dc' },
  signal: { background: '#141311', title: '#f3eee6', text: '#e4dcd0', accent: '#d24a2c', surface: '#1f1d1a' },
  ledger: { background: '#f3f4f0', title: '#17202a', text: '#2c3640', accent: '#1b555c', surface: '#e8eae4' },
  slate: { background: '#f2f3f5', title: '#1c2430', text: '#2f3742', accent: '#8a5a32', surface: '#e6e8ec' },
  paper: { background: '#f7f4ee', title: '#1a1814', text: '#3a352e', accent: '#2c3e6b', surface: '#efeae0' },
};

export const RAIL_LAYOUTS = new Set(['title', 'section', 'closing', 'statement']);

// Canonical image wells on the 13.333" × 7.5" surface. Models guess x/y/w/h;
// snapSlideImageLayout puts every picture on one of these and reflows text.
export const IMAGE_BLEED = { x: 0, y: 0, w: 6.15, h: 7.5 };
export const IMAGE_BLEED_RIGHT = { x: 7.183, y: 0, w: 6.15, h: 7.5 };
export const IMAGE_WELL = { x: 0.64, y: 1.55, w: 7.15, h: 4.85 };
export const IMAGE_WELL_RIGHT = { x: 5.54, y: 1.55, w: 7.15, h: 4.85 };
const TEXT_COL_LEFT = { x: 0.95, w: 6.15 };
const TEXT_COL_RIGHT = { x: 6.55, w: 6.15 };
const BLEED_LAYOUTS = new Set(['title', 'section', 'statement', 'closing', 'split_picture']);
const WELL_LAYOUTS = new Set(['picture']);

const boxesOverlap = (a, b, gap = 0.06) => (
  (Number(a?.x) || 0) < (Number(b?.x) || 0) + (Number(b?.w) || 0) - gap
  && (Number(a?.x) || 0) + (Number(a?.w) || 0) > (Number(b?.x) || 0) + gap
  && (Number(a?.y) || 0) < (Number(b?.y) || 0) + (Number(b?.h) || 0) - gap
  && (Number(a?.y) || 0) + (Number(a?.h) || 0) > (Number(b?.y) || 0) + gap
);

const wellScore = (el, well) => (
  Math.abs((Number(el?.x) || 0) - well.x)
  + Math.abs((Number(el?.y) || 0) - well.y)
  + Math.abs((Number(el?.w) || 0) - well.w)
  + Math.abs((Number(el?.h) || 0) - well.h)
);

const pickImageWell = (image, layout, slideIndex) => {
  const cx = (Number(image?.x) || 0) + (Number(image?.w) || 0) / 2;
  const right = image?.imageSide === 'right' || image?.side === 'right'
    || (image?.imageSide !== 'left' && image?.side !== 'left' && cx > SLIDE_WIDTH / 2);
  if (WELL_LAYOUTS.has(layout)) return right ? IMAGE_WELL_RIGHT : IMAGE_WELL;
  if (BLEED_LAYOUTS.has(layout) || slideIndex === 0 || layout === 'content' || layout === 'blank') {
    return right ? IMAGE_BLEED_RIGHT : IMAGE_BLEED;
  }
  return right ? IMAGE_WELL_RIGHT : IMAGE_WELL;
};

const reflowAroundImage = (element, imageBox) => {
  if (!element || element.type === 'image') return element;
  const imageOnRight = (Number(imageBox.x) || 0) >= 6.5;
  const col = imageOnRight ? TEXT_COL_LEFT : TEXT_COL_RIGHT;
  if (element.type === 'shape' && (Number(element.w) || 0) < 0.2 && (Number(element.x) || 0) < 0.25) {
    return element;
  }
  if (element.type === 'shape' && (Number(element.h) || 0) < 0.15) {
    return boxesOverlap(element, imageBox, 0.02) ? { ...element, x: col.x } : element;
  }
  if (!boxesOverlap(element, imageBox, 0.02) && (Number(element.w) || 0) <= 7.2) {
    return element;
  }
  return { ...element, x: col.x, w: Math.min(Number(element.w) || col.w, col.w) };
};

/**
 * Snap guessed image boxes onto the 16:9 grid and move overlapping text into
 * the remaining column. One designed picture per slide; extra stamps that
 * collide with the well are dropped.
 */
export const snapSlideImageLayout = (slide, slideIndex = 0) => {
  if (slide?.preserveLayout === true) return slide;
  const elements = Array.isArray(slide?.elements) ? slide.elements.filter(Boolean) : [];
  const images = elements.filter(element => element.type === 'image');
  if (!images.length) return slide;

  const primary = {
    ...[...images].sort((a, b) => (
      (Number(b.w) || 0) * (Number(b.h) || 0) - (Number(a.w) || 0) * (Number(a.h) || 0)
    ))[0],
    imageSide: slide?.imageSide || images[0]?.imageSide,
  };
  if ((Number(primary.w) || 0) >= 12 && (Number(primary.h) || 0) >= 6) {
    return slide;
  }
  let layout = slide?.layout || (slideIndex === 0 ? 'title' : 'content');
  if (layout === 'content' || layout === 'blank') {
    layout = slideIndex === 0 ? 'title' : 'split_picture';
  }

  const well = pickImageWell(primary, layout, slideIndex);
  const snappedImage = { ...primary, x: well.x, y: well.y, w: well.w, h: well.h };
  const nextElements = elements.map((element) => {
    if (element === primary || element.id === primary.id) return snappedImage;
    if (element.type === 'image') return boxesOverlap(element, well, 0.02) ? null : element;
    return reflowAroundImage(element, well);
  }).filter(Boolean);

  return { ...slide, layout, elements: nextElements };
};

/** Resolve a theme name to its palette, falling back for unknown names. */
export const getPresentationTheme = (name) => (
  Object.prototype.hasOwnProperty.call(PRESENTATION_THEMES, name)
    ? PRESENTATION_THEMES[name]
    : PRESENTATION_THEMES[DEFAULT_PRESENTATION_THEME]
);

/**
 * Element position as percentages of the slide surface, so the caller can lay
 * it out absolutely without knowing the rendered pixel size.
 */
export const elementBox = (element, slideSize) => ({
  left: ((Number(element?.x) || 0) / (slideSize?.width || SLIDE_WIDTH)) * 100,
  top: ((Number(element?.y) || 0) / (slideSize?.height || SLIDE_HEIGHT)) * 100,
  width: ((Number(element?.w) || 0) / (slideSize?.width || SLIDE_WIDTH)) * 100,
  height: ((Number(element?.h) || 0) / (slideSize?.height || SLIDE_HEIGHT)) * 100,
});

/**
 * Titles and metrics take the theme's title color; everything else takes body
 * text. A per-element `color` overrides both and is stored without a `#`.
 */
export const elementTextColor = (element, theme) => {
  if (element?.color) return `#${String(element.color).replace(/^#/, '')}`;
  return element?.role === 'title' || element?.role === 'metric' ? theme.title : theme.text;
};

/** Fill for `shape` elements — named tokens resolve against the theme. */
export const shapeFill = (element, theme) => {
  const fill = element?.fill;
  if (fill === 'accent') return theme.accent;
  if (fill === 'surface') return theme.surface || theme.background;
  if (fill === 'title') return theme.title;
  if (fill === 'text') return theme.text;
  if (/^#?[0-9a-f]{6}$/i.test(fill || '')) {
    return fill.startsWith('#') ? fill : `#${fill}`;
  }
  return theme.accent;
};

/**
 * Lift leftover document-sized type so an already-saved deck reads as a
 * keynote in the editor and viewer. Matches the server-side normalizer:
 * old 18/28pt defaults grow; explicit designed sizes stay put.
 */
export const liftPresentationElement = (element) => {
  if (element?.preserveTypography === true) return element;
  if (!element || (element.type !== 'text' && element.type !== 'bullets')) return element;
  const declared = Number(element.fontSize);
  const hasDeclared = Number.isFinite(declared);
  let fontSize = hasDeclared ? declared : (element.type === 'bullets' ? 22 : 22);
  let h = Number(element.h) || 0;
  if (element.type === 'bullets' && (!hasDeclared || declared < 20)) {
    fontSize = 22;
  }
  if (element.type === 'text' && element.role === 'title' && (!hasDeclared || declared === 28 || declared <= 18)) {
    fontSize = 36;
    if (!h || h < 0.85) h = 0.95;
  }
  if (element.type === 'text' && element.role === 'metric' && (!hasDeclared || declared <= 32)) {
    fontSize = 48;
    if (!h || h < 1) h = 1.2;
  }
  if (element.type === 'text' && (element.role === 'body' || element.role === 'subtitle') && (!hasDeclared || declared <= 18)) {
    fontSize = 22;
  }
  if (element.type === 'text' && element.role === 'quote' && (!hasDeclared || declared <= 26)) {
    fontSize = 32;
    if (!h || h < 1.6) h = 2.2;
  }
  if (fontSize === (hasDeclared ? declared : null) && h === (Number(element.h) || 0)) return element;
  return { ...element, fontSize, ...(h ? { h } : {}) };
};

/**
 * Elements as they should paint: lifted type plus an accent rule under a
 * top title when the saved spec has no chrome (legacy bland decks).
 */
export const displaySlideElements = (slide, slideIndex = 0, { snapImages = true, liftType = true } = {}) => {
  if (slide?.preserveLayout === true) return (slide.elements || []).filter(Boolean);
  const snapped = snapImages ? snapSlideImageLayout(slide, slideIndex) : slide;
  const elements = (Array.isArray(snapped?.elements) ? snapped.elements : [])
    .map((element) => fitCompactSlideText(liftType ? liftPresentationElement(element) : element));
  if (!liftType || elements.some(element => element?.type === 'shape')) return elements;
  const titleEl = elements.find(element => (
    element?.type === 'text' && element?.role === 'title' && (Number(element.y) || 0) <= 1.6
  ));
  if (!titleEl) return elements;
  return [
    ...elements,
    {
      id: `${slide?.id || 'slide'}-display-rule`,
      type: 'shape',
      fill: 'accent',
      x: titleEl.x,
      y: Math.min(7.2, (Number(titleEl.y) || 0) + (Number(titleEl.h) || 0.95) + 0.06),
      w: 1.7,
      h: 0.07,
      radius: 0,
      displayOnly: true,
    },
  ];
};

/** Proportional font size in px for a slide rendered `slideWidthPx` wide. */
export const elementFontSize = (element, slideWidthPx, slideSize) => {
  const declared = Number(element?.fontSize) || 22;
  const reference = slideSize?.width ? slideSize.width * 72 : SLIDE_REFERENCE_WIDTH;
  return Math.max(4, (declared * slideWidthPx) / reference);
};

/** Bullet items are stored as bare strings; the glyph is presentation only. */
export const bulletLines = (items) => (
  (Array.isArray(items) ? items : []).slice(0, MAX_BULLETS).map(item => `• ${item}`)
);

/**
 * The text a slide contributes to a readable outline, used by the mobile
 * viewer to show legible content beside the necessarily tiny slide preview.
 */
export const slideOutline = (slide) => {
  const elements = Array.isArray(slide?.elements) ? slide.elements : [];
  const titleElement = elements.find(element => element?.role === 'title' && element?.type === 'text');
  const body = [];
  for (const element of elements) {
    if (element === titleElement) continue;
    if (element?.type === 'bullets') {
      for (const item of (Array.isArray(element.items) ? element.items : [])) {
        if (String(item).trim()) body.push(String(item));
      }
    } else if (element?.type === 'text' && String(element.text || '').trim()) {
      body.push(String(element.text));
    } else if (element?.type === 'chart') {
      if (element.spec?.title) body.push(String(element.spec.title));
      for (const series of (Array.isArray(element.spec?.series) ? element.spec.series : []).slice(0, MAX_CHART_SERIES)) {
        const points = (Array.isArray(series?.data) ? series.data : []).slice(0, MAX_CHART_POINTS)
          .filter(point => point && typeof point.y === 'number' && Number.isFinite(point.y));
        if (points.length) body.push(`${String(series.name || 'Series')}: ${points.map(point => `${String(point.x ?? '')} ${point.y}`).join(', ')}`);
      }
    } else if (element?.type === 'table') {
      const rows = [element.header, ...(Array.isArray(element.rows) ? element.rows : [])];
      for (const row of rows) {
        if (Array.isArray(row) && row.length) body.push(row.map(cell => String(cell ?? '')).join(' | '));
      }
    }
  }
  return {
    title: String(titleElement?.text || slide?.title || '').trim(),
    body,
    imageCount: elements.filter(element => element?.type === 'image' && element?.src).length,
    speakerNotes: String(slide?.speakerNotes || '').trim(),
  };
};

/**
 * Shape an incoming spec for read-only rendering. Specs are normalized
 * server-side on every save and generation, so this only guards against a
 * missing or malformed payload rather than re-validating every field.
 */
export const normalizeDeckForViewing = (spec, fallbackTitle = 'Presentation') => {
  const slides = Array.isArray(spec?.slides) ? spec.slides : [];
  const size = spec?.slideSize;
  const validSize = [size?.width, size?.height].every(value => Number.isFinite(value) && value >= 1 && value <= 56);
  return {
    title: String(spec?.title || fallbackTitle),
    subtitle: String(spec?.subtitle || ''),
    ...(validSize ? { slideSize: { width: size.width, height: size.height } } : {}),
    theme: getPresentationTheme(spec?.theme),
    themeName: Object.prototype.hasOwnProperty.call(PRESENTATION_THEMES, spec?.theme)
      ? spec.theme
      : DEFAULT_PRESENTATION_THEME,
    slides: slides.map((slide, index) => ({
      id: slide?.id || `slide-${index + 1}`,
      ...(slide?.preserveLayout === true ? { preserveLayout: true } : {}),
      layout: slide?.layout || (index === 0 ? 'title' : 'content'),
      title: String(slide?.title || ''),
      speakerNotes: String(slide?.speakerNotes || ''),
      elements: (Array.isArray(slide?.elements) ? slide.elements : []).filter(Boolean),
    })),
  };
};
